// 🃏 **M 컷 «모션그래픽» 카드 조립기** — 부품 6개를 세이프존 안에 놓고 `t01` 로 재생한다(2026-09-28 · 1단계).
//
// ★한 벌★ — `cinematic-motion-draw.drawDataScene` 이 `motionStyle === "kit"` 일 때만 이 함수로 온다.
//   내보내기(mediabunny)·정밀편집 카드·카드 사진 굽기가 전부 그 함수를 지나므로 여기가 곧 세 경로다.
// ★자리는 세이프존 SSOT(`safe-zone.ts` core 층)★ — 종전 카드는 캔버스 여백 6% 만 봤고, 실측 세이프존
//   (9:16 안전 21.09~73.18% · 우 14.75% 버튼 열)과 어긋났다. 새 스타일은 처음부터 core 안에만 글자를 둔다.
// ★크기 하한은 `motion-card-layout` 의 것★ — `MOTION_CARD_MIN_TITLE_PX`·`MOTION_CARD_MIN_LINE_PX` 를
//   그대로 쓴다(산식 복제 금지 계약). 넘치면 줄이되 하한 밑으로는 안 내려가고, 그래도 넘치면 다 그린다.
// ★시간은 `t01`(0~1) 하나★ — M 컷엔 박자 정보가 없어 컷 길이를 균등 분할한 «가짜 박자»(`beatGrid`).
//   `t01 >= 1` 이면 모든 부품이 완성 상태다(사진 굽기 = 마지막 프레임).
import { titleFontCss } from "../caption-fonts";
import {
  MOTION_CARD_MIN_LINE_PX,
  MOTION_CARD_MIN_TITLE_PX,
  fitTitleLines,
  wrapWordsByMeasure,
} from "../motion-card-layout";
import { nearestSafeAspect, safeInset, type SafeAspect } from "../safe-zone";
import { DURATION_MS, KIT_DEFAULT_CUT_SEC, lin, msToT01 } from "./spring";
import {
  checkList,
  colorCut,
  kinetic,
  pairCopy,
  stamp,
  stepReveal,
  type CheckRow,
  type KineticLine,
  type KitBox,
} from "./parts";
import { pickMotionKitRecipe, scheduleBeats, splitByWords, type MotionKitRecipe } from "./pick";

export interface MotionKitCardInput {
  title: string;
  lines: readonly string[];
  characterImg?: HTMLImageElement | null;
  /** 컷 길이(초) — ms 기본값(`DURATION_MS`)·노출 하한(`minHoldSec`)을 `t01` 로 바꾸는 데 쓴다. 없으면 `KIT_DEFAULT_CUT_SEC`. */
  durationSec?: number;
}

/** 이 카드가 글자를 두는 자리 — 세이프존 core 층(px). 검사(`measure.ts`)도 같은 함수를 부른다.
 *  ★안쪽으로 접는다★(왼·위 = 올림, 오른·아래 = 내림) — `safeInsetPx` 의 반올림은 경계를 1px 밖으로 낼 수 있어
 *  `isInsideSafe`(비율 판정)와 어긋난다(16:9 좌 4.17% → 53px 가 4.14%). 정수 px 로 접되 언제나 안쪽이다. */
export function motionKitSafeBox(W: number, H: number): { aspect: SafeAspect; box: KitBox; ui: KitBox } {
  const aspect = nearestSafeAspect(W, H);
  const inner = (s: { top: number; bottom: number; left: number; right: number }): KitBox => {
    const x = Math.ceil(W * s.left);
    const y = Math.ceil(H * s.top);
    return { x, y, w: Math.floor(W * (1 - s.right)) - x, h: Math.floor(H * (1 - s.bottom)) - y };
  };
  return { aspect, box: inner(safeInset(aspect, "core")), ui: inner(safeInset(aspect, "ui")) };
}

/** 데이터 줄 폰트 — 종전 카드와 같은 스택(사진 굽기·합본이 이미 이 폰트를 준비한다). */
export const kitLineFont = (px: number): string => `600 ${px}px "Noto Sans KR", "Apple SD Gothic Neo", sans-serif`;

const LINE_H = 1.22;

interface TitleBlock { px: number; font: string; lines: KineticLine[]; bottom: number; chars: number }

function layoutTitle(
  measure: (font: string, text: string) => number,
  fontId: string,
  title: string,
  box: KitBox,
  basePx: number,
  maxLines: number,
  y: number,
): TitleBlock {
  const fit = fitTitleLines({
    text: title,
    maxW: box.w,
    baseSize: basePx,
    maxLines,
    minScale: Math.min(1, MOTION_CARD_MIN_TITLE_PX / basePx),
    measure: (t, size) => measure(titleFontCss(fontId, size, 800), t),
  });
  const px = Math.max(MOTION_CARD_MIN_TITLE_PX, fit.size);
  const font = titleFontCss(fontId, px, 800);
  const lines = fit.lines.map((text, i) => ({ text, x: box.x, y: y + i * px * LINE_H }));
  const chars = fit.lines.reduce((n, l) => n + Array.from(l).length, 0);
  return { px, font, lines, bottom: lines.length ? y + lines.length * px * LINE_H : y, chars };
}

/** 제목 글자 등장 일정(t01) — 글자 간격 30ms(글자가 많으면 전체 700ms 안에 끝나게 줄인다) · 글자 하나 진입 500ms. */
function titleSchedule(chars: number, cutSec: number): { start: number; perChar: number; dur: number; end: number } {
  const start = -msToT01(150, cutSec); // 0초 프레임에 제목 첫 글자들이 이미 들어오는 중(종전 카드와 같은 출발 · 검은 프레임 0)
  const dur = msToT01(DURATION_MS.enter, cutSec);
  const perCharMs = chars > 0 ? Math.min(DURATION_MS.perChar, 700 / chars) : DURATION_MS.perChar;
  const perChar = msToT01(perCharMs, cutSec);
  return { start, perChar, dur, end: start + chars * perChar + dur };
}

/**
 * 🎨 그린다. `motionStyle` 판정은 호출자(`drawDataScene`)가 했고, 부품 조합은 여기서 `pickMotionKitRecipe` 로
 * 정한다(순수 · 데이터에 실을 필요 없음). 반환값은 검사·테스트용 요약이다.
 */
export function drawMotionKitCard(
  ctx: CanvasRenderingContext2D,
  motion: MotionKitCardInput,
  W: number,
  H: number,
  t01: number,
  fontId = "default",
): MotionKitRecipe {
  const t = t01 >= 1 ? 1 : t01 <= 0 ? 0 : t01;
  // 📏 크기 기준 길이 — 세로(720×1280)·4:5(1080×1350)는 W 그대로, 가로(1280×720)는 H×1.3=936 → 제목 67px(화면 높이 9.3%).
  //   종전 `min(W,H)` 는 가로에서 720 이 되어 제목 52px(7.2%)로 종전 카드(74px)보다 작았다(부모 세션 육안 09-28).
  const S = Math.min(W, H * 1.3);
  const cutSec = motion.durationSec && motion.durationSec > 0 ? motion.durationSec : KIT_DEFAULT_CUT_SEC;
  const ms = (v: number) => msToT01(v, cutSec);
  const sec = (v: number) => (cutSec > 0 ? v / cutSec : 0);
  const recipe = pickMotionKitRecipe({ title: motion.title || "", lines: motion.lines });
  const frame: KitBox = { x: 0, y: 0, w: W, h: H };
  const { box: core, ui } = motionKitSafeBox(W, H);
  const hasChar = !!motion.characterImg && motion.characterImg.width > 0;
  // 🎭 인물 그림이 오른쪽 아래 서면 그만큼 글자 폭을 양보한다(종전 카드의 `W × 0.26` 예약과 같은 뜻).
  const box: KitBox = hasChar ? { ...core, w: core.w - W * 0.24 } : core;
  let activeFont = "";
  const measure = (font: string, text: string) => {
    if (activeFont !== font) { ctx.font = font; activeFont = font; }
    return ctx.measureText(text).width;
  };
  // 📝 화면 글자 블록 ≤ 6어절 — 넘치면 먼저 어절로 줄을 나눈다(그 뒤 폭 줄바꿈).
  const lines = motion.lines.flatMap((l) => splitByWords(String(l ?? "")));

  colorCut(ctx, frame, t, { color: recipe.color, start: 0, dur: ms(180), pulsePeriod: sec(2.0) });
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  if (recipe.kind === "pair") {
    // 두 줄 대구 — 제목이 위, 한 줄이 흰 띠 위에 브랜드색으로.
    const basePx = Math.round(S * 0.084);
    const title = layoutTitle(measure, fontId, motion.title || "", box, basePx, 3, box.y + box.h * 0.2);
    const sch = titleSchedule(title.chars, cutSec);
    kinetic(ctx, box, t, { lines: title.lines, font: title.font, px: title.px, color: "#ffffff", start: sch.start, perChar: sch.perChar, dur: sch.dur, lineGap: ms(60) });
    if (lines.length) {
      const px = title.px;
      const font = titleFontCss(fontId, px, 800);
      const wrapped = wrapWordsByMeasure((tx) => measure(font, tx), lines[0], box.w - px * 0.4);
      const y2 = title.bottom + px * 0.55;
      const pairLines = wrapped.map((text, i) => ({ text, x: box.x + px * 0.19, y: y2 + i * px * 1.45 }));
      const clipH = wrapped.length * px * 1.45 + px * 0.4;
      activeFont = ""; // pairCopy 가 font 를 직접 세운다 — 측정 캐시를 비운다
      pairCopy(ctx, box, t, {
        lines: pairLines, font, px, accent: recipe.color,
        start: Math.min(sch.end + ms(150), Math.max(0, 1 - sec(1.5))), dur: ms(DURATION_MS.enter),
        clip: { x: box.x - px * 0.3, y: y2 - px * 0.25, w: box.w + px * 0.6, h: clipH },
      });
    }
  } else if (recipe.kind === "check") {
    const title = layoutTitle(measure, fontId, motion.title || "", box, Math.round(S * 0.072), 2, box.y);
    const sch = titleSchedule(title.chars, cutSec);
    kinetic(ctx, box, t, { lines: title.lines, font: title.font, px: title.px, color: "#ffffff", start: sch.start, perChar: sch.perChar, dur: sch.dur, lineGap: ms(60) });
    // 행 배치 — 넘치면 글자를 하한까지 줄인다(하한 밑으로는 안 간다 · 그래도 넘치면 다 그린다).
    const top = title.bottom + S * 0.06;
    const stampPx = Math.round(S * 0.08);
    const reserveStamp = recipe.stamp ? stampPx * 2.1 : 0;
    const buildRows = (px: number, compact = false): { rows: CheckRow[]; bottom: number; font: string } => {
      const font = kitLineFont(px);
      const cb = px * 1.35;
      const rowH = compact ? 2.1 : 2.8;
      const rowGap = compact ? 0.3 : 0.55;
      const textX = px * 0.7 + cb + px * 0.7;
      const rows: CheckRow[] = [];
      let y = top;
      lines.forEach((line, i) => {
        const wrapped = wrapWordsByMeasure((tx) => measure(font, tx), line, box.w - textX - px * 0.5);
        const h = Math.max(px * rowH, wrapped.length * px * 1.3 + px * (compact ? 0.6 : 1.0));
        const y0 = y + (h - wrapped.length * px * 1.3) / 2 + px * 0.05;
        rows.push({ box: { x: box.x, y, w: box.w, h }, lines: wrapped.map((text, k) => ({ text, x: box.x + textX, y: y0 + k * px * 1.3 })), start: 0 });
        y += h + px * rowGap;
        void i;
      });
      return { rows, bottom: y, font };
    };
    let px = Math.max(MOTION_CARD_MIN_LINE_PX, Math.round(S * 0.046));
    let built = buildRows(px);
    while (built.bottom + reserveStamp > box.y + box.h && px > MOTION_CARD_MIN_LINE_PX) {
      px -= 1;
      built = buildRows(px);
    }
    // 하한에서도 넘치면 촘촘 배치(행 높이 2.1·간격 0.3)로 한 번 더 — 그래도 넘치면 다 그린다(자르지 않는다).
    if (built.bottom + reserveStamp > box.y + box.h) built = buildRows(px, true);
    // 🥁 박자 = 노출 하한(max 1.5s, 글자수/6)을 채우며 배분 · 도장 자리 350ms 는 남긴다. 컷이 짧으면 균등 압축(전부 나온다).
    const bodyStart = (sch.end + ms(150)) * cutSec;
    const bodyEnd = cutSec - (recipe.stamp ? 0.35 : 0);
    const plan = scheduleBeats(lines, bodyStart, Math.max(bodyStart, bodyEnd));
    built.rows.forEach((r, i) => { r.start = sec(plan.startsSec[i]); });
    activeFont = "";
    checkList(ctx, box, t, { rows: built.rows, dur: ms(DURATION_MS.enter), font: built.font, px, accent: recipe.color, tickSec: cutSec });
    if (recipe.stamp) {
      const halfW = Math.max(stampPx * 2.3, measure(titleFontCss(fontId, stampPx, 800), recipe.stamp) / 2 + stampPx * 0.6);
      // 도장은 core 아래쪽 — 행이 거기까지 내려왔으면 행 밑으로(그래도 core 밖이면 core 바닥에 맞춘다).
      const cyWant = Math.max(built.bottom + stampPx * 1.05, box.y + box.h - stampPx * 1.05);
      const cy = Math.min(cyWant, box.y + box.h - stampPx * 1.05);
      activeFont = "";
      const lastRow = built.rows.length ? built.rows[built.rows.length - 1].start + ms(DURATION_MS.enter) : sch.end;
      stamp(ctx, box, t, { text: recipe.stamp, font: titleFontCss(fontId, stampPx, 800), px: stampPx, cx: box.x + box.w - halfW - stampPx * 0.3, cy, start: Math.min(Math.max(lastRow, 1 - sec(0.35)), Math.max(0, 1 - ms(DURATION_MS.emphasis))), dur: ms(DURATION_MS.emphasis), halfW, tickSec: cutSec });
    }
  } else {
    // 단계형 — 제목 · 진행 막대 · 가운데 도형 · 아래 캡션이 **누적**(번호 라벨 없음).
    const title = layoutTitle(measure, fontId, motion.title || "", box, Math.round(S * 0.064), 2, box.y);
    const sch = titleSchedule(title.chars, cutSec);
    kinetic(ctx, box, t, { lines: title.lines, font: title.font, px: title.px, color: "#ffffff", start: sch.start, perChar: sch.perChar, dur: sch.dur, lineGap: ms(60) });
    const barH = Math.max(6, Math.round(S * 0.016));
    const bar: KitBox = { x: box.x, y: title.bottom + S * 0.045, w: box.w, h: barH };
    // 캡션 크기 — 전 단계가 다 쌓여도 core 안에 들어가게 줄인다(하한 42).
    const buildCaptions = (px: number) => {
      const font = titleFontCss(fontId, px, 800);
      const fits = lines.map((line) => wrapWordsByMeasure((tx) => measure(font, tx), line, box.w));
      const totalLines = fits.reduce((n, f) => n + f.length, 0);
      // 띠 높이 = 첫 줄 위 여백 0.1 + (줄 수−1)×행간 + 글자 높이 + 아래 여백 0.05 — 마지막 글자 아랫변이 띠 안(=core 안)에 들어간다.
      return { px, font, fits, totalLines, bandH: totalLines > 0 ? px * 0.1 + (totalLines - 1) * px * LINE_H + px * 1.05 : 0 };
    };
    let capPx = Math.round(S * 0.062);
    let caps = buildCaptions(capPx);
    const minFigure = S * 0.12;
    while (bar.y + barH + S * 0.03 + minFigure + S * 0.03 + caps.bandH > box.y + box.h && capPx > MOTION_CARD_MIN_TITLE_PX) {
      capPx -= 1;
      caps = buildCaptions(capPx);
    }
    const band: KitBox = { x: box.x - capPx * 0.2, y: box.y + box.h - caps.bandH, w: box.w + capPx * 0.4, h: caps.bandH };
    let cy = band.y + capPx * 0.1;
    const steps = caps.fits.map((f) => {
      const linesOut = f.map((text, i) => ({ text, x: box.x, y: cy + i * capPx * LINE_H }));
      cy += f.length * capPx * LINE_H;
      return linesOut;
    });
    const figTop = bar.y + barH + S * 0.03;
    const figBottom = band.y - S * 0.03;
    const size = Math.max(0, Math.min(S * 0.28, (figBottom - figTop) * 0.8, box.w * 0.6));
    const bodyStart = (sch.end + ms(150)) * cutSec;
    const plan = scheduleBeats(lines, bodyStart, Math.max(bodyStart, cutSec));
    activeFont = "";
    stepReveal(ctx, box, t, {
      steps, beats: plan.startsSec.map(sec), dur: ms(DURATION_MS.enter),
      font: caps.font, px: capPx, color: recipe.color, bar,
      figure: { cx: box.x + box.w / 2, cy: (figTop + figBottom) / 2, size: figBottom - figTop > minFigure * 0.5 ? size : 0 },
      captionClip: band,
    });
  }

  const img = motion.characterImg;
  if (hasChar && img) {
    const cw = W * 0.24;
    const ch = cw * (img.height / img.width || 1.3);
    ctx.save();
    ctx.globalAlpha = lin(t, ms(600), ms(600 + DURATION_MS.enter));
    ctx.drawImage(img, ui.x + ui.w - cw, ui.y + ui.h - ch, cw, ch);
    ctx.restore();
  }
  return recipe;
}
